/**
 * ============================================================================
 * VEDANG JYOTISHAM V6.5 — BIRTH TIME RECTIFICATION (BTR) CONTRACT & TYPES
 * ============================================================================
 * Specification: VJ-BTR-V65-SPEC-001 v3.0
 * Phase 1 Foundation: Two-Axis Uncertainty, Candidate Tuples, Search Coverage
 * Audit, Categorical Evidence Model, and 4-State Lifecycle Provenance.
 *
 * Strict Rule: Contains contracts, interfaces, and immutable types only.
 * No duplicate astronomical calculation logic or heuristic scoring.
 * ============================================================================
 */

import { PlanetName, ZodiacSign } from './kundali';

// ============================================================================
// 1. TWO-AXIS BIRTH UNCERTAINTY CONTRACT
// ============================================================================

export type DateCertainty = 'EXACT' | 'BOUNDED_RANGE' | 'UNKNOWN_MULTI_YEAR';

export type TimePrecisionLevel = 'Exact' | 'Minutes' | 'QuarterHour' | 'Hour' | 'Approximate';

export type BirthTimeSourceType =
  | 'Exact official record'
  | 'Hospital birth certificate'
  | 'Municipal civil record'
  | 'Contemporaneous family diary'
  | 'Family recollection'
  | 'Approximate memory'
  | 'Rounded clock time'
  | 'Inferred from life events'
  | 'Completely unknown';

export interface AxisATimeUncertainty {
  /** Earliest plausible local clock time (HH:mm:ss) */
  earliestLocalTime: string;
  /** Latest plausible local clock time (HH:mm:ss) */
  latestLocalTime: string;
  /** Documented or recorded local clock time (HH:mm:ss) */
  reportedLocalTime: string;
  /** Source category for recorded clock time */
  timeSource: BirthTimeSourceType;
  /** Precision level of source record */
  precisionLevel: TimePrecisionLevel;
}

export interface AxisBDateUncertainty {
  /** Earliest plausible civil calendar date (YYYY-MM-DD) */
  earliestLocalDate: string;
  /** Latest plausible civil calendar date (YYYY-MM-DD) */
  latestLocalDate: string;
  /** Documented or reported civil calendar date (YYYY-MM-DD) */
  reportedLocalDate: string;
  /** Certainty classification */
  certainty: DateCertainty;
  /** Provenance description or documentation citation */
  dateSource: string;
}

export interface GeographicContext {
  /** Latitude in decimal degrees (-90 to +90) */
  latitude: number;
  /** Longitude in decimal degrees (-180 to +180) */
  longitude: number;
  /** Canonical display place name */
  placeName: string;
  /** Authoritative IANA timezone identifier (e.g. 'Asia/Kolkata', 'America/New_York') */
  timezoneId: string;
  /** Optional standard offset in minutes for verification against IANA resolution */
  timezoneOffsetMinutes?: number;
}

export interface TwoAxisBirthUncertainty {
  /** Axis A: Local clock-time uncertainty on candidate dates */
  axisATime: AxisATimeUncertainty;
  /** Axis B: Civil calendar date uncertainty (up to ±2 years) */
  axisBDate: AxisBDateUncertainty;
  /** Birth geographic coordinates and IANA timezone context */
  location: GeographicContext;
}

// ============================================================================
// 2. FULLY QUALIFIED IMMUTABLE CANDIDATE TUPLE
// ============================================================================

export interface CandidateTuple {
  /** Local civil calendar date (YYYY-MM-DD) */
  readonly localDate: string;
  /** Local civil clock time (HH:mm:ss) */
  readonly localClockTime: string;
  /** Latitude in decimal degrees */
  readonly latitude: number;
  /** Longitude in decimal degrees */
  readonly longitude: number;
  /** Geographical location name */
  readonly placeName: string;
  /** Authoritative IANA timezone identifier */
  readonly timezoneId: string;
  /** Exact timezone and DST offset in seconds resolved for this instant */
  readonly utcOffsetSeconds: number;
  /** Normalized UTC instant in ISO 8601 format */
  readonly utcInstant: string;
  /** Candidate generation pass provenance */
  readonly sourceProvenance: string;
  /** Engine version tracking string */
  readonly calculationVersion: string;
}

// ============================================================================
// 3. TEMPORAL REGION & SEARCH COVERAGE AUDIT
// ============================================================================

export type TemporalRegionType = 'EVALUATED' | 'PRUNED' | 'UNEVALUATED';

export interface TemporalRegion {
  /** Region start instant in ISO 8601 UTC */
  startUtc: string;
  /** Region end instant in ISO 8601 UTC */
  endUtc: string;
  /** Region classification */
  regionType: TemporalRegionType;
  /** Astrological or computational reason */
  reason: string;
  /** Sampling step size in minutes (for evaluated regions) */
  stepSizeMinutes?: number;
  /** Number of candidate points evaluated or bypassed */
  candidatesCount?: number;
}

export type SearchCoverageStatus =
  | 'EXHAUSTIVE'              // Every theoretical minute evaluated across envelope
  | 'PRUNED_COMPLETE'         // Adaptive pruning executed with all skipped regions formally verified
  | 'TRUNCATED_BY_BUDGET'     // Search halted due to evaluation or execution time budget limit
  | 'INSUFFICIENT_DATA';      // Envelope undefined or input validation failed

export interface SearchCoverageAudit {
  /** Requested input envelope spans */
  requestedEnvelope: {
    axisBDateRange: { start: string; end: string };
    axisATimeRange: { start: string; end: string };
    totalTheoreticalSeconds: number;
  };
  /** Evaluation and computational budget tracking */
  budgetLimits: {
    maxEvaluationsAllowed: number;
    actualEvaluationsExecuted: number;
    maxTimeMsAllowed: number;
    actualTimeMsTaken: number;
    budgetExceeded: boolean;
  };
  /** Multi-stage cascade execution trace */
  stagesExecuted: {
    stageName: 'MacroEphemeris' | 'LunarDashaGate' | 'MesoLagna' | 'MicroPlateau';
    stepMinutes: number;
    evaluatedCount: number;
    prunedCount: number;
    pruningCriteria: string;
  }[];
  /** Explicit ledger of evaluated, pruned, and unevaluated regions */
  evaluatedRegions: TemporalRegion[];
  prunedRegions: TemporalRegion[];
  unevaluatedRegions: TemporalRegion[];
  /** Overall coverage completeness verdict */
  coverageStatus: SearchCoverageStatus;
  /** Completeness ratio between 0.0 and 1.0 */
  completenessRatio: number;
  /** Audit generation timestamp */
  auditTimestamp: string;
  /** Engine build version identifier */
  engineVersion: string;
}

// ============================================================================
// 4. CATEGORICAL EVIDENCE & VARIABLE CLUSTERING CONTRACT
// ============================================================================

export type CategoricalEvidenceStatus = 'SUPPORTS' | 'NEUTRAL' | 'CONTRADICTS' | 'INSUFFICIENT_DATA';

export type VariableClusterId =
  | 'CLUSTER_I_ASCENDANT'    // Diurnal Earth rotation: D1, D9, D10 Lagnas, KP sub-lords
  | 'CLUSTER_II_LUNAR'       // Geocentric Moon motion: Vimshottari Mahadasha/Antardasha, Nakshatra
  | 'CLUSTER_III_PLANETARY'  // Planetary longitude topology: Jaimini Chara Karakas
  | 'CLUSTER_IV_TRANSIT';    // Historical Transits (Guru-Shani Gochar) to Natal Frame

export type LifeEventTier = 'TIER_A' | 'TIER_B' | 'TIER_C';

export type LifeEventCategory =
  | 'MARRIAGE'
  | 'CHILDREN'
  | 'CAREER'
  | 'BUSINESS'
  | 'RELOCATION'
  | 'PROPERTY'
  | 'HEALTH'
  | 'FAMILY'
  | 'EDUCATION'
  | 'FINANCE'
  | 'LEGAL'
  | 'SPIRITUAL'
  | 'OTHER';

export interface CategoricalEvidenceItem {
  /** Unique shastric rule identifier */
  ruleId: string;
  /** Rule display name */
  ruleName: string;
  /** Classical treatise or shastra citation (e.g., 'BPHS Ch. 46') */
  shastraSource: string;
  /** Physical variable cluster */
  variableCluster: VariableClusterId;
  /** Names of shared underlying variables to prevent double-counting */
  correlatedVariables: string[];
  /** Associated life event domain */
  domain: LifeEventCategory;
  /** Evaluated categorical status */
  status: CategoricalEvidenceStatus;
  /** Associated event quality tier */
  eventTier: LifeEventTier;
  /** ID of corresponding life event */
  eventId: string;
  /** Human-readable astrological rationale */
  rationale: string;
  /** Contradiction severity if status is CONTRADICTS */
  contradictionSeverity?: 'CRITICAL' | 'MODERATE' | 'NONE';
  /** Whether rule relies strictly on verified canonical calculation core */
  isVerifiedCalculation: boolean;
}

export interface BtrLifeEventInput {
  /** Unique life event identifier */
  id: string;
  /** Canonical life event category (13 user-selectable categories) */
  category: LifeEventCategory;
  /** Civil event date (YYYY-MM-DD or partial YYYY / YYYY-MM) */
  date: string;
  /** Optional civil event clock time (HH:mm:ss) */
  time?: string;
  /** Evidence confidence tier */
  tier: LifeEventTier;
  /** Human-readable event description */
  description?: string;
  /** Optional geographical location of the event */
  location?: string;
}

export const BTR_ERROR_INVALID_EVENT_PRECISION = 'BTR_INVALID_EVENT_PRECISION';

export interface ClusterEvidenceSummary {
  clusterId: VariableClusterId;
  supportingCount: number;
  neutralCount: number;
  contradictingCount: number;
  insufficientCount: number;
  clusterStatus: CategoricalEvidenceStatus;
  primaryTechniques: string[];
}

export interface PlateauEvidenceEvaluation {
  plateauInterval: AstrologicalPlateauInterval;
  evaluatedEventsCount: number;
  evidenceItems: CategoricalEvidenceItem[];
  clusterSummaries: Record<VariableClusterId, ClusterEvidenceSummary>;
  independentClustersSupportedCount: number;
  isDisqualified: boolean;
  disqualificationReason?: string;
  criticalContradictionsCount: number;
  moderateContradictionsCount: number;
  convergenceVerdict: 'CONVERGENT' | 'PARTIAL' | 'DISQUALIFIED' | 'INCONCLUSIVE';
}

// ============================================================================
// 5. SEARCH OUTCOME STATUSES & INCOMPLETE COVERAGE
// ============================================================================

export type SearchOutcomeStatus =
  | 'SUCCESS_CONVERGENT_INTERVAL'
  | 'SUCCESS_MULTIPLE_CANDIDATE_CLUSTERS'
  | 'INCONCLUSIVE_INSUFFICIENT_EVIDENCE'
  | 'INCONCLUSIVE_CONTRADICTORY_EVIDENCE'
  | 'INCONCLUSIVE_DISPARATE_PEAKS'
  | 'ACTION_REQUIRED_PARTIAL_COVERAGE'
  | 'ACTION_REQUIRED_UNRESOLVED_TIMEZONE'
  | 'ACTION_REQUIRED_INVALID_INPUT';

// ============================================================================
// 6. FOUR BTR STATES CONTRACT (PRESERVING V6.4 PROVENANCE COMPATIBILITY)
// ============================================================================

export type BtrLifecycleState =
  | 'RECORDED'              // Immutable baseline
  | 'RECTIFIED_INTERVAL'     // Analytical bounded plateau
  | 'WORKING'               // Provisional working hypothesis (unconfirmed)
  | 'CONFIRMED'             // Deliberately confirmed by user/acharya
  | 'STALE';                // Historical record invalidated by subsequent edit

export interface AstrologicalPlateauInterval {
  /** Local start clock time (HH:mm:ss) */
  startLocalTime: string;
  /** Local end clock time (HH:mm:ss) */
  endLocalTime: string;
  /** Formatted interval string (e.g., '12:04:15 - 12:07:30') */
  intervalString: string;
  /** Continuous interval span in seconds */
  spanSeconds: number;
  /** Non-selected mathematical reference midpoint for display only */
  mathematicalMidpointReference: string;
  /** Invariant chart structure elements confirmed uniform across interval */
  invariantsHeld: {
    d1AscendantSign: ZodiacSign;
    d9NavamshaSign: ZodiacSign;
    d10DashamshaSign: ZodiacSign;
    kpCuspSubLordHouse1: PlanetName;
    kpCuspSubLordHouse7: PlanetName;
    kpCuspSubLordHouse10: PlanetName;
  };
  /** Optional civil calendar date for multi-day search (YYYY-MM-DD) */
  localDate?: string;
  /** ISO UTC start timestamp (inclusive) */
  startUtcInstant?: string;
  /** ISO UTC end timestamp (exclusive) */
  endUtcInstant?: string;
  /** Resolved UTC offset in minutes for this plateau */
  timezoneOffsetMinutes?: number;
}

export interface BtrProvenanceRecord {
  /** State at the time of entry */
  state: BtrLifecycleState;
  /** ISO timestamp of state transition */
  timestamp: string;
  /** Authenticated user or acharya identifier */
  actorId?: string;
  /** Transition explanation or justification */
  reason: string;
  /** Snapshot of active parameters at transition */
  snapshot?: {
    recordedTime: string;
    workingTime?: string | null;
    confirmedTime?: string | null;
    rectifiedInterval?: string | null;
  };
}

export interface FourBtrStates {
  /** Immutable recorded time from civil documentation (HH:mm:ss) */
  recordedTime: string;
  /** Calculated continuous astrological plateau interval */
  rectifiedInterval: AstrologicalPlateauInterval | null;
  /** Operational working hypothesis explicitly selected by user/acharya */
  workingTime: string | null;
  /** Formally confirmed birth time (null unless explicitly confirmed) */
  confirmedTime: string | null;
  /** Active lifecycle state */
  lifecycleState: BtrLifecycleState;
  /** Provisional flag: true whenever workingTime is unconfirmed */
  isProvisional: boolean;
  /** Complete append-only audit provenance ledger */
  provenanceLedger: BtrProvenanceRecord[];
}

// ============================================================================
// 7. END-TO-END SEARCH ORCHESTRATION CONTRACT (PHASE 5)
// ============================================================================

export interface BtrSearchOptions {
  /** Maximum candidate evaluations allowed (default 2500) */
  maxEvaluationsAllowed?: number;
  /** Maximum execution time allowed in ms (default 400ms target) */
  maxTimeMsAllowed?: number;
  /** Step size in seconds for adaptive coarse scanning (default 120s) */
  stepSeconds?: number;
  /** Whether to perform 1-second dense verification of candidate plateaus */
  denseVerify?: boolean;
  /** Optional custom ayanamsha system (default 'Lahiri') */
  ayanamsha?: string;
  /** Node convention ('mean' | 'true') */
  nodeConvention?: 'mean' | 'true';
  /** Explicit opt-in declaration for broad/multi-day candidate search (AGENTS.md §7.2) */
  allowMultiDayCascade?: boolean;
  /** Explicit opt-in declaration for broad candidate interval search up to ±2 years */
  allowBroadSearch?: boolean;
}

export interface BtrSearchResult {
  /** Unambiguous search outcome classification */
  outcomeStatus: SearchOutcomeStatus;
  /** Optional machine-readable error code for invalid inputs or execution halts */
  errorCode?: string;
  /** Primary convergent candidate plateaus supported across >= 2 clusters without critical contradictions */
  convergentPlateaus: PlateauEvidenceEvaluation[];
  /** Plateaus disqualified by Tier A critical contradictions */
  disqualifiedPlateaus: PlateauEvidenceEvaluation[];
  /** All evaluated candidate plateaus discovered in the search window */
  allEvaluatedPlateaus: PlateauEvidenceEvaluation[];
  /** Full accounting of evaluated, pruned, and unevaluated temporal regions */
  coverageAudit: SearchCoverageAudit;
  /** Immutable 4-state lifecycle provenance record for this run */
  provenanceRecord: BtrProvenanceRecord;
  /** Active FourBtrStates snapshot */
  stateSnapshot: FourBtrStates;
  /** ISO 8601 UTC execution timestamp */
  timestamp: string;
  /** Detailed human-readable diagnostic message explaining outcome */
  message: string;
}

